// client/src/components/SportAndEvaluationSelection.tsx
import React from 'react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import Card from './ui/Card';

export interface SelectableItem {
  id: string;
  name: string;
  icon: JSX.Element;
  isComingSoon?: boolean;
}

export interface SportAndEvaluationSelectionProps {
  sportTypes: SelectableItem[];
  evaluationTypes: Record<string, SelectableItem[]>;
  selectedSportType: string | null;
  onSelectSportType: (sportId: string) => void;
  selectedEvaluationType: string | null;
  onSelectEvaluationType: (evalId: string) => void;
}

const SportAndEvaluationSelection: React.FC<
  SportAndEvaluationSelectionProps
> = ({
  sportTypes,
  evaluationTypes,
  selectedSportType,
  onSelectSportType,
  selectedEvaluationType,
  onSelectEvaluationType,
}) => {
  const selectedSport = sportTypes.find(
    (sport) => sport.id === selectedSportType
  );
  const selectedEval =
    selectedSportType && evaluationTypes[selectedSportType]
      ? evaluationTypes[selectedSportType].find(
          (evalType) => evalType.id === selectedEvaluationType
        )
      : null;

  return (
    <Card className='p-4 relative z-[100]'>
      <div className='flex flex-col sm:flex-row gap-4'>
        {/* Sport Selection Dropdown */}
        <div className='flex-1'>
          <label className='block text-sm font-medium text-text-primary-light dark:text-text-primary-dark mb-2'>
            Select Sport
          </label>
          <Menu as='div' className='relative'>
            <MenuButton className='inline-flex w-full justify-between items-center gap-x-2 rounded-lg bg-surface-light dark:bg-surface-darker px-4 py-3 text-sm font-semibold text-text-primary-light dark:text-text-primary-dark border border-border-light dark:border-black backdrop-blur-sm hover:bg-surface-light/80 dark:hover:bg-surface-darker/80 transition-all duration-200'>
              <span>
                {selectedSport ? selectedSport.name : 'Choose a sport...'}
              </span>
              <ChevronDownIcon
                aria-hidden='true'
                className='w-5 h-5 text-text-secondary-light dark:text-text-secondary-dark'
              />
            </MenuButton>

            <MenuItems
              modal={false}
              portal={true}
              anchor='bottom start'
              transition
              className='z-[200] mt-2 w-[var(--button-width)] origin-top rounded-lg bg-surface-light dark:bg-surface-dark shadow-lg border border-border-light dark:border-black transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in'
            >
              <div className='py-1'>
                {sportTypes.map((sport) => (
                  <MenuItem key={sport.id} disabled={sport.isComingSoon}>
                    <button
                      onClick={() =>
                        !sport.isComingSoon && onSelectSportType(sport.id)
                      }
                      className={`w-full px-4 py-2 text-sm text-left transition-colors ${
                        sport.isComingSoon
                          ? 'text-text-secondary-light dark:text-text-secondary-dark opacity-50 cursor-not-allowed'
                          : 'text-text-primary-light dark:text-text-primary-dark data-[focus]:bg-surface-light/50 dark:data-[focus]:bg-surface-darker/80 data-[focus]:outline-none'
                      } ${selectedSportType === sport.id ? 'bg-surface-light/50 dark:bg-surface-darker/80' : ''}`}
                      disabled={sport.isComingSoon}
                    >
                      <span>{sport.name}</span>
                      {sport.isComingSoon && (
                        <span className='ml-2 text-xs opacity-70'>(Soon)</span>
                      )}
                    </button>
                  </MenuItem>
                ))}
              </div>
            </MenuItems>
          </Menu>
        </div>

        {/* Evaluation Type Selection Dropdown */}
        {selectedSportType && evaluationTypes[selectedSportType] && (
          <div className='flex-1'>
            <label className='block text-sm font-medium text-text-primary-light dark:text-text-primary-dark mb-2'>
              Select Evaluation Type
            </label>
            <Menu as='div' className='relative'>
              <MenuButton className='inline-flex w-full justify-between items-center gap-x-2 rounded-lg bg-surface-light dark:bg-surface-darker px-4 py-3 text-sm font-semibold text-text-primary-light dark:text-text-primary-dark border border-border-light dark:border-black backdrop-blur-sm hover:bg-surface-light/80 dark:hover:bg-surface-darker/80 transition-all duration-200'>
                <span>
                  {selectedEval
                    ? selectedEval.name
                    : 'Choose evaluation type...'}
                </span>
                <ChevronDownIcon
                  aria-hidden='true'
                  className='w-5 h-5 text-text-secondary-light dark:text-text-secondary-dark'
                />
              </MenuButton>

              <MenuItems
                modal={false}
                portal={true}
                anchor='bottom start'
                transition
                className='z-[200] mt-2 w-[var(--button-width)] origin-top rounded-lg bg-surface-light dark:bg-surface-dark shadow-lg border border-border-light dark:border-black transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in'
              >
                <div className='py-1'>
                  {evaluationTypes[selectedSportType].map((evalType) => (
                    <MenuItem
                      key={evalType.id}
                      disabled={evalType.isComingSoon}
                    >
                      <button
                        onClick={() =>
                          !evalType.isComingSoon &&
                          onSelectEvaluationType(evalType.id)
                        }
                        className={`w-full px-4 py-2 text-sm text-left transition-colors ${
                          evalType.isComingSoon
                            ? 'text-text-secondary-light dark:text-text-secondary-dark opacity-50 cursor-not-allowed'
                            : 'text-text-primary-light dark:text-text-primary-dark data-[focus]:bg-surface-light/50 dark:data-[focus]:bg-surface-darker/80 data-[focus]:outline-none'
                        } ${selectedEvaluationType === evalType.id ? 'bg-surface-light/50 dark:bg-surface-darker/80' : ''}`}
                        disabled={evalType.isComingSoon}
                      >
                        <span>{evalType.name}</span>
                        {evalType.isComingSoon && (
                          <span className='ml-2 text-xs opacity-70'>
                            (Soon)
                          </span>
                        )}
                      </button>
                    </MenuItem>
                  ))}
                </div>
              </MenuItems>
            </Menu>
          </div>
        )}
      </div>
    </Card>
  );
};

export default SportAndEvaluationSelection;
